// Device detection and capability utilities

// Extend Navigator interface for non-standard properties
interface ExtendedNavigator extends Navigator {
  deviceMemory?: number;
  connection?: {
    effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
    downlink?: number;
    addEventListener: (type: string, listener: () => void) => void;
    removeEventListener: (type: string, listener: () => void) => void;
  };
  mozConnection?: ExtendedNavigator['connection'];
  webkitConnection?: ExtendedNavigator['connection'];
}

export interface DeviceCapabilities {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  hasTouch: boolean;
  deviceMemory: number; // in GB
  hardwareConcurrency: number; // CPU cores
  connectionType: 'slow-2g' | '2g' | '3g' | '4g' | 'wifi' | 'unknown';
  isLowEndDevice: boolean;
  maxImageDimension: number;
  imageQuality: number;
  chunkSize: number;
}

// Detect if device is mobile
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;

  // Check user agent
  const mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(navigator.userAgent);

  // Check screen size
  const isMobileScreen = window.innerWidth <= 768;

  // Check touch support
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  return isMobileUA || (isMobileScreen && hasTouch);
}

// Detect if device is tablet
export function isTabletDevice(): boolean {
  if (typeof window === 'undefined') return false;

  const tabletRegex = /iPad|Android.*Tablet|Kindle|Silk/i;
  const isTabletUA = tabletRegex.test(navigator.userAgent);

  // Check screen size (tablet range)
  const isTabletScreen = window.innerWidth > 768 && window.innerWidth <= 1024;

  return isTabletUA || isTabletScreen;
}

// Get device memory in GB
export function getDeviceMemory(): number {
  if (typeof window === 'undefined') return 4; // Default 4GB

  const memory = (navigator as ExtendedNavigator).deviceMemory;
  return memory || 4; // Default to 4GB if not available
}

// Get hardware concurrency (CPU cores)
export function getHardwareConcurrency(): number {
  if (typeof window === 'undefined') return 4; // Default 4 cores

  return navigator.hardwareConcurrency || 4;
}

// Get connection type
export function getConnectionType(): DeviceCapabilities['connectionType'] {
  if (typeof window === 'undefined') return 'unknown';

  const nav = navigator as ExtendedNavigator;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection;

  if (!connection) return 'unknown';

  // Check effective type
  if (connection.effectiveType) {
    return connection.effectiveType as DeviceCapabilities['connectionType'];
  }

  // Fallback to checking downlink speed
  if (connection.downlink) {
    if (connection.downlink < 0.25) return 'slow-2g';
    if (connection.downlink < 0.75) return '2g';
    if (connection.downlink < 2) return '3g';
    return '4g';
  }

  return 'unknown';
}

// Check if device is low-end
export function isLowEndDevice(): boolean {
  const memory = getDeviceMemory();
  const cores = getHardwareConcurrency();
  const isMobile = isMobileDevice();
  const connection = getConnectionType();

  // Consider device low-end if:
  // - Less than 4GB RAM
  // - Less than 4 CPU cores
  // - Mobile with slow connection
  // - Connection is 2G or slower
  return (
    memory < 4 ||
    cores < 4 ||
    (isMobile && ['slow-2g', '2g'].includes(connection)) ||
    ['slow-2g', '2g'].includes(connection)
  );
}

// Get device capabilities
export function getDeviceCapabilities(): DeviceCapabilities {
  const mobile = isMobileDevice();
  const tablet = isTabletDevice();
  const desktop = !mobile && !tablet;
  const memory = getDeviceMemory();
  const cores = getHardwareConcurrency();
  const connection = getConnectionType();
  const lowEnd = isLowEndDevice();

  // Determine optimal settings based on device
  let maxDimension = 1920;
  let quality = 0.85;
  let chunkSize = 1024 * 1024; // 1MB chunks

  if (lowEnd) {
    maxDimension = 1024;
    quality = 0.7;
    chunkSize = 256 * 1024; // 256KB chunks
  } else if (mobile) {
    maxDimension = 1280;
    quality = 0.75;
    chunkSize = 512 * 1024; // 512KB chunks
  } else if (tablet) {
    maxDimension = 1600;
    quality = 0.8;
    chunkSize = 768 * 1024; // 768KB chunks
  }

  // Further reduce for slow connections
  if (['slow-2g', '2g'].includes(connection)) {
    maxDimension = Math.min(maxDimension, 800);
    quality = Math.min(quality, 0.6);
    chunkSize = Math.min(chunkSize, 128 * 1024); // 128KB chunks
  } else if (connection === '3g') {
    maxDimension = Math.min(maxDimension, 1280);
    quality = Math.min(quality, 0.7);
    chunkSize = Math.min(chunkSize, 256 * 1024); // 256KB chunks
  }

  return {
    isMobile: mobile,
    isTablet: tablet,
    isDesktop: desktop,
    hasTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    deviceMemory: memory,
    hardwareConcurrency: cores,
    connectionType: connection,
    isLowEndDevice: lowEnd,
    maxImageDimension: maxDimension,
    imageQuality: quality,
    chunkSize,
  };
}

// Monitor connection changes
export function onConnectionChange(
  callback: (connection: DeviceCapabilities['connectionType']) => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const nav = navigator as ExtendedNavigator;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection;

  if (!connection) return () => {};

  const handleChange = () => {
    callback(getConnectionType());
  };

  connection.addEventListener('change', handleChange);

  return () => {
    connection.removeEventListener('change', handleChange);
  };
}

// Get optimal image processing settings
export interface ImageProcessingSettings {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  format: 'webp' | 'jpeg';
  progressive: boolean;
  chunkSize: number;
}

export function getOptimalImageSettings(): ImageProcessingSettings {
  const capabilities = getDeviceCapabilities();

  // Use WebP if supported (better compression)
  const supportsWebP = detectWebPSupport();

  return {
    maxWidth: capabilities.maxImageDimension,
    maxHeight: capabilities.maxImageDimension,
    quality: capabilities.imageQuality,
    format: supportsWebP ? 'webp' : 'jpeg',
    progressive: true, // Always use progressive encoding
    chunkSize: capabilities.chunkSize,
  };
}

// Detect WebP support
let webpSupported: boolean | null = null;

export function detectWebPSupport(): boolean {
  if (webpSupported !== null) return webpSupported;

  if (typeof window === 'undefined') {
    webpSupported = false;
    return webpSupported;
  }

  // Create a tiny WebP image and check if it loads
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    webpSupported = false;
    return webpSupported;
  }

  try {
    webpSupported = canvas.toDataURL('image/webp').indexOf('image/webp') === 5;
  } catch {
    webpSupported = false;
  }

  return webpSupported;
}

// Memory-efficient image processing for mobile
export function shouldUseOffscreenCanvas(): boolean {
  if (typeof window === 'undefined') return false;

  // Check if OffscreenCanvas is available and device has enough memory
  const hasOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';
  const memory = getDeviceMemory();

  return hasOffscreenCanvas && memory >= 4;
}

// Get recommended batch size for parallel processing
export function getRecommendedBatchSize(): number {
  const capabilities = getDeviceCapabilities();

  if (capabilities.isLowEndDevice) return 1; // Process one at a time
  if (capabilities.isMobile) return 2;
  if (capabilities.isTablet) return 3;
  return 4; // Desktop can handle more
}
